import type { AssessmentResults, RiskFactor, TeamReadiness, SuccessMetric, CostModel } from '../types';

export const calculateRiskAssessment = (results: AssessmentResults): RiskFactor[] => {
  const risks: RiskFactor[] = [];
  
  // Team readiness risk based on team vital sign
  if (results.vitalSigns.team <= 4) {
    risks.push({
      risk: "Low Team Capacity",
      severity: "high",
      mitigation: "Invest in team training and hire AI specialists before implementation",
      impact: "Could delay implementation by 2-3 months and reduce adoption rates"
    });
  } else if (results.vitalSigns.team <= 6) {
    risks.push({
      risk: "Team Adaptation Challenges",
      severity: "medium", 
      mitigation: "Implement comprehensive change management and training programs",
      impact: "May require additional training budget and extended rollout timeline"
    });
  }

  // Technology integration risk based on operations score
  if (results.quadrantScores.operations <= 5) {
    risks.push({
      risk: "System Integration Complexity",
      severity: "high",
      mitigation: "Conduct technical audit and plan phased integration approach",
      impact: "Potential system downtime and integration delays"
    });
  }

  // Budget risk based on AI experience and budget combination
  const budgetRisk = assessBudgetRisk(results.aiBudget, results.aiExperience);
  if (budgetRisk) risks.push(budgetRisk);

  // Sales process disruption risk
  if (results.vitalSigns.sales <= 5 && results.primaryChallenge === "Growing revenue") {
    risks.push({
      risk: "Sales Process Disruption",
      severity: "medium",
      mitigation: "Implement AI tools gradually with extensive sales team training",
      impact: "Temporary reduction in sales efficiency during transition period"
    });
  }

  // Data quality risk based on content and audience scores
  if (results.vitalSigns.content <= 6 || results.vitalSigns.audience <= 6) {
    risks.push({
      risk: "Insufficient Data Quality",
      severity: "medium",
      mitigation: "Implement data cleaning and collection processes before AI deployment",
      impact: "Reduced AI effectiveness and accuracy in initial months"
    });
  }

  return risks;
};

const assessBudgetRisk = (budget: string, experience: string): RiskFactor | null => {
  const isLowBudget = budget === "under_5k" || budget === "5k_25k";
  const isBeginnerExperience = experience === "beginner";
  
  if (isLowBudget && isBeginnerExperience) {
    return {
      risk: "Insufficient Budget for Learning Curve",
      severity: "high", 
      mitigation: "Start with free/low-cost tools and invest in training before scaling",
      impact: "May need to extend timeline and reduce initial scope"
    };
  }
  
  if (isLowBudget) {
    return {
      risk: "Limited Tool Selection",
      severity: "medium",
      mitigation: "Focus on high-ROI tools and consider phased implementation",
      impact: "May miss opportunities for comprehensive automation"
    };
  }
  
  return null;
};

export const calculateTeamReadiness = (results: AssessmentResults): TeamReadiness => {
  const teamScore = results.vitalSigns.team;
  const operationsScore = results.quadrantScores.operations;
  const averageReadiness = (teamScore + operationsScore) / 2;
  
  const strengths: string[] = [];
  const gaps: string[] = [];
  const recommendations: string[] = [];
  
  // Analyze strengths
  if (teamScore >= 7) strengths.push("Strong team capacity and skills");
  if (operationsScore >= 7) strengths.push("Solid operational processes");
  if (results.aiExperience !== "beginner") strengths.push("Previous AI/technology experience");
  if (results.ethicsImportance === "critical") strengths.push("Strong ethical framework awareness");
  
  // Identify gaps
  if (teamScore <= 5) gaps.push("Limited team capacity for new initiatives");
  if (operationsScore <= 5) gaps.push("Weak operational foundations");
  if (results.aiExperience === "beginner") gaps.push("Limited AI knowledge and experience");
  if (results.vitalSigns.content <= 6) gaps.push("Insufficient content and data systems");
  
  // Generate recommendations
  if (gaps.includes("Limited team capacity for new initiatives")) {
    recommendations.push("Hire dedicated AI implementation manager or consultant");
    recommendations.push("Invest in team training programs before tool deployment");
  }
  
  if (gaps.includes("Limited AI knowledge and experience")) {
    recommendations.push("Start with AI literacy training for key stakeholders");
    recommendations.push("Partner with experienced AI implementation specialists");
  }
  
  if (gaps.includes("Weak operational foundations")) {
    recommendations.push("Strengthen core business processes before AI integration");
    recommendations.push("Implement project management and change management frameworks");
  }
  
  // Default recommendations for all levels
  recommendations.push("Establish AI governance and success metrics framework");
  recommendations.push("Create cross-functional AI implementation team");
  
  const level: 'low' | 'medium' | 'high' = 
    averageReadiness >= 7 ? 'high' : 
    averageReadiness >= 5 ? 'medium' : 'low';
  
  return {
    score: Math.round(averageReadiness * 10) / 10,
    level,
    strengths,
    gaps,
    recommendations
  };
};

export const calculateSuccessMetrics = (results: AssessmentResults): SuccessMetric[] => {
  const metrics: SuccessMetric[] = [];
  
  // Revenue-focused metrics
  if (results.primaryQuadrant === "Revenue" || results.primaryChallenge === "Growing revenue") {
    metrics.push({
      metric: "Revenue Growth Rate",
      baseline: "Current monthly revenue",
      target: "25-40% increase",
      timeframe: "6 months",
      measurement: "Monthly recurring revenue tracking"
    });
    
    metrics.push({
      metric: "Sales Conversion Rate", 
      baseline: `${results.vitalSigns.sales * 10}% estimated current rate`,
      target: `${Math.min(results.vitalSigns.sales * 10 + 15, 95)}% target rate`,
      timeframe: "3 months",
      measurement: "CRM pipeline analysis"
    });
  }
  
  // Operations efficiency metrics
  if (results.quadrantScores.operations <= 6 || results.primaryChallenge === "Improving efficiency") {
    metrics.push({
      metric: "Process Automation Rate",
      baseline: "Manual processes currently",
      target: "60-80% automation",
      timeframe: "4 months", 
      measurement: "Time tracking and process audits"
    });
    
    metrics.push({
      metric: "Team Productivity",
      baseline: "Current hours per key task",
      target: "40-60% time reduction",
      timeframe: "3 months",
      measurement: "Task completion time analysis"
    });
  }
  
  // Customer engagement metrics
  if (results.primaryQuadrant === "Audience" || results.vitalSigns.audience >= 7) {
    metrics.push({
      metric: "Lead Quality Score",
      baseline: `${results.vitalSigns.leads}/10 current quality`,
      target: `${Math.min(results.vitalSigns.leads + 3, 10)}/10 target quality`,
      timeframe: "2 months",
      measurement: "Lead scoring and conversion tracking"
    });
  }
  
  // Brand and content metrics
  if (results.primaryQuadrant === "Brand" || results.vitalSigns.content >= 7) {
    metrics.push({
      metric: "Content Engagement Rate",
      baseline: "Current engagement metrics",
      target: "50-75% improvement",
      timeframe: "3 months",
      measurement: "Social media and website analytics"
    });
  }
  
  // Universal ROI metric
  metrics.push({
    metric: "Overall ROI",
    baseline: "Initial AI investment",
    target: "300-500% return",
    timeframe: "12 months",
    measurement: "Cost savings + revenue increase vs investment"
  });
  
  return metrics;
};

export const calculateCostModel = (results: AssessmentResults): CostModel => {
  const budgetMapping: { [key: string]: number } = {
    "under_5k": 3000,
    "5k_25k": 15000,
    "25k_50k": 37500,
    "50k_100k": 75000,
    "100k_plus": 150000
  };
  
  const totalBudget = budgetMapping[results.aiBudget] || 15000;
  
  // Experience multiplier affects implementation costs
  const experienceMultiplier = results.aiExperience === "beginner" ? 1.3 : 
                              results.aiExperience === "intermediate" ? 1.1 : 1.0;
  
  // Complexity multiplier based on operations score
  const complexityMultiplier = results.quadrantScores.operations <= 5 ? 1.4 :
                              results.quadrantScores.operations <= 7 ? 1.2 : 1.0;
  
  const adjustedBudget = totalBudget * experienceMultiplier * complexityMultiplier;
  
  // Budget breakdown percentages
  const breakdown = {
    software: Math.round(adjustedBudget * 0.45), // 45% for software tools
    training: Math.round(adjustedBudget * 0.20), // 20% for training
    implementation: Math.round(adjustedBudget * 0.25), // 25% for implementation
    maintenance: Math.round(adjustedBudget * 0.10) // 10% for ongoing maintenance
  };
  
  // ROI calculations based on business scores and primary focus
  const baseROI = calculateBaseROI(results);
  
  const roi = {
    month3: Math.round(baseROI * 0.3 * 100) / 100, // 30% of full ROI by month 3
    month6: Math.round(baseROI * 0.7 * 100) / 100, // 70% of full ROI by month 6  
    month12: Math.round(baseROI * 100) / 100 // Full ROI by month 12
  };
  
  // Payback period in months
  const monthlyReturn = adjustedBudget * (baseROI / 12);
  const paybackPeriod = Math.round(adjustedBudget / monthlyReturn * 10) / 10;
  
  return {
    totalInvestment: Math.round(adjustedBudget),
    breakdownByCategory: breakdown,
    roi,
    paybackPeriod: Math.max(paybackPeriod, 2) // Minimum 2 months
  };
};

const calculateBaseROI = (results: AssessmentResults): number => {
  let baseROI = 3.0; // Default 3x ROI
  
  // Adjust based on primary focus area
  if (results.primaryQuadrant === "Revenue") {
    baseROI += 2.0; // Revenue focus typically sees higher ROI
  }
  
  // Adjust based on current performance gaps (bigger gaps = bigger opportunity)
  const averageScore = Object.values(results.quadrantScores).reduce((a, b) => a + b, 0) / 4;
  if (averageScore <= 5) {
    baseROI += 2.0; // Low performers have more room for improvement
  } else if (averageScore <= 7) {
    baseROI += 1.0;
  }
  
  // Adjust based on AI experience (experienced teams implement faster)
  if (results.aiExperience === "advanced") {
    baseROI += 1.0;
  } else if (results.aiExperience === "beginner") {
    baseROI -= 0.5;
  }
  
  // Adjust based on team readiness
  if (results.vitalSigns.team >= 8) {
    baseROI += 0.5;
  }
  
  return Math.max(baseROI, 2.0); // Minimum 2x ROI
};

export const enhanceAssessmentResults = (results: AssessmentResults): AssessmentResults => {
  return {
    ...results,
    riskAssessment: calculateRiskAssessment(results),
    teamReadiness: calculateTeamReadiness(results),
    successMetrics: calculateSuccessMetrics(results),
    costModel: calculateCostModel(results)
  };
};